import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../contractorApi';

const fmt = (n) => Number(n || 0).toLocaleString();
const fmtDate = (s) => {
    if (!s) return '—';
    try { return new Date(s).toLocaleDateString('ja-JP', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return s; }
};

const ESTIMATE_STATUS = {
    submitted: { label: '提出済み', bg: '#fff3e0', color: '#e65100' },
    accepted:  { label: '承認済み', bg: '#e8f5e9', color: '#2e7d32' },
    rejected:  { label: '差し戻し', bg: '#fce4ec', color: '#c62828' },
};
const INVOICE_STATUS = {
    issued: { label: '発行済み', bg: '#fff3e0', color: '#e65100' },
    paid:   { label: '支払済み', bg: '#e8f5e9', color: '#2e7d32' },
};

const FileIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
);
const EmptyIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
    </svg>
);

const ContractorDocumentsPage = () => {
    const contractorId = localStorage.getItem('contractor_id');
    const navigate = useNavigate();
    const [tab, setTab] = useState('estimates');
    const [estimates, setEstimates] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!contractorId) return;
        setLoading(true);
        Promise.all([
            api.get(`estimates/by_contractor/${contractorId}`),
            api.get(`invoices/by_contractor/${contractorId}`),
        ]).then(([eRes, iRes]) => {
            setEstimates(eRes.data);
            setInvoices(iRes.data);
        }).catch(console.error)
          .finally(() => setLoading(false));
    }, [contractorId]);

    const TAB_STYLE = (active) => ({
        flex: 1, padding: '12px 8px',
        background: 'none', border: 'none', cursor: 'pointer',
        color: active ? '#005c53' : '#6b7280',
        fontWeight: active ? 700 : 400,
        fontSize: '0.9rem',
        borderBottom: active ? '2px solid #005c53' : '2px solid transparent',
        transition: 'color 0.15s',
    });

    const renderRow = (item, type) => {
        const projectId = item.project_id;
        const customer = item.project?.customer;
        const customerName = customer
            ? `${customer.last_name} ${customer.first_name} 様`
            : `顧客 #${item.project?.customer_id || '—'}`;
        const workType = item.project?.condition?.work_types?.join('・') || '—';
        const statusMap = type === 'estimate' ? ESTIMATE_STATUS : INVOICE_STATUS;
        const s = statusMap[item.status] || { label: item.status, bg: '#f5f5f5', color: '#666' };
        const date = fmtDate(item.created_at);
        const actionPath = type === 'estimate'
            ? `/contractor/estimate/create/${projectId}`
            : `/contractor/invoice/create/${projectId}`;

        return (
            <div key={item.id} style={{
                background: '#fff', borderRadius: 8,
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                padding: '16px 20px',
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 12,
            }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1a1a2e' }}>{customerName}</span>
                        <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.74rem', fontWeight: 500, background: s.bg, color: s.color }}>
                            {s.label}
                        </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                        <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>{workType}</span>
                        <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>{date}</span>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
                    <span style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', whiteSpace: 'nowrap' }}>
                        ¥{fmt(item.amount)}
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            onClick={() => navigate(`/contractor/project/${projectId}`)}
                            style={{ padding: '7px 12px', background: '#fff', color: '#6b7280', border: '1px solid #e0e4ea', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            案件詳細
                        </button>
                        <button
                            onClick={() => navigate(actionPath)}
                            style={{ padding: '7px 14px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            書類を見る
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const currentList = tab === 'estimates' ? estimates : invoices;
    const type = tab === 'estimates' ? 'estimate' : 'invoice';

    return (
        <div style={{ minHeight: '80vh', background: '#f5f6f8' }}>

            {/* ページヘッダー */}
            <div style={{ background: '#141c3d', padding: '32px 0' }}>
                <div style={{ maxWidth: 880, margin: '0 auto', padding: '0 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ color: '#fff', opacity: 0.7 }}><FileIcon /></span>
                    <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', margin: 0 }}>書類管理</h1>
                </div>
            </div>

            <div style={{ maxWidth: 880, margin: '0 auto', padding: '28px 16px' }}>

                {/* タブ */}
                <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 20 }}>
                    <div style={{ display: 'flex', borderBottom: '1px solid #e0e4ea' }}>
                        <button style={TAB_STYLE(tab === 'estimates')} onClick={() => setTab('estimates')}>
                            見積書 {estimates.length > 0 && <span style={{ marginLeft: 6, fontSize: '0.8rem', color: '#9ca3af' }}>({estimates.length}件)</span>}
                        </button>
                        <button style={TAB_STYLE(tab === 'invoices')} onClick={() => setTab('invoices')}>
                            請求書 {invoices.length > 0 && <span style={{ marginLeft: 6, fontSize: '0.8rem', color: '#9ca3af' }}>({invoices.length}件)</span>}
                        </button>
                    </div>
                </div>

                {/* コンテンツ */}
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                        <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    </div>
                ) : currentList.length === 0 ? (
                    <div style={{ background: '#fff', borderRadius: 8, padding: '60px 24px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}><EmptyIcon /></div>
                        <p style={{ color: '#9ca3af', fontSize: '0.9rem', margin: 0 }}>
                            {tab === 'estimates' ? 'まだ見積書がありません' : 'まだ請求書がありません'}
                        </p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {currentList.map(item => renderRow(item, type))}
                    </div>
                )}
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default ContractorDocumentsPage;
